import type { ECharts } from "echarts";

const PDF_PAGE_WIDTH = 842;
const PDF_PAGE_HEIGHT = 595;
const PDF_MARGIN = 40;

type CapturedChart = {
  dataUrl: string;
  width: number;
  height: number;
  image: HTMLImageElement;
};

type PdfPlacement = {
  x: number;
  y: number;
  width: number;
  height: number;
};

function sanitizeFileName(fileName: string): string {
  const cleaned = fileName.trim().replace(/[^\w.-]+/g, "-").replace(/-+/g, "-");
  return cleaned.replace(/^-|-$/g, "") || "bar-3d-chart";
}

function downloadBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName;
  anchor.style.display = "none";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("Could not read the chart image."));
    image.src = src;
  });
}

async function captureChart(chart: ECharts): Promise<CapturedChart> {
  const dataUrl = chart.getDataURL({
    type: "png",
    pixelRatio: 2,
    backgroundColor: "#ffffff",
  });

  if (!dataUrl) {
    throw new Error("Chart image capture returned no data.");
  }

  const image = await loadImage(dataUrl);
  if (image.naturalWidth === 0 || image.naturalHeight === 0) {
    throw new Error("Chart image capture returned an empty image.");
  }

  return {
    dataUrl,
    width: image.naturalWidth,
    height: image.naturalHeight,
    image,
  };
}

function buildSvgDocument(dataUrl: string, width: number, height: number): string {
  return [
    `<?xml version="1.0" encoding="UTF-8"?>`,
    `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">`,
    `<rect width="100%" height="100%" fill="#ffffff"/>`,
    `<image href="${dataUrl}" width="${width}" height="${height}" preserveAspectRatio="none"/>`,
    `</svg>`,
  ].join("");
}

async function imageToJpeg(image: HTMLImageElement, width: number, height: number): Promise<Uint8Array> {
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext("2d");
  if (!context) {
    throw new Error("Canvas export is not supported in this browser.");
  }

  context.fillStyle = "#ffffff";
  context.fillRect(0, 0, width, height);
  context.drawImage(image, 0, 0, width, height);

  const blob = await new Promise<Blob | null>((resolve) => {
    canvas.toBlob(resolve, "image/jpeg", 0.92);
  });
  if (!blob) {
    throw new Error("Could not encode the chart image for PDF.");
  }

  return new Uint8Array(await blob.arrayBuffer());
}

function fitImageOnPage(
  imageWidth: number,
  imageHeight: number,
  pageWidth: number,
  pageHeight: number,
): PdfPlacement {
  const maxWidth = pageWidth - PDF_MARGIN * 2;
  const maxHeight = pageHeight - PDF_MARGIN * 2;
  const scale = Math.min(maxWidth / imageWidth, maxHeight / imageHeight);
  const width = imageWidth * scale;
  const height = imageHeight * scale;

  return {
    x: (pageWidth - width) / 2,
    y: (pageHeight - height) / 2,
    width,
    height,
  };
}

function encodePdfText(value: string): Uint8Array {
  return new TextEncoder().encode(value);
}

function concatBytes(parts: Uint8Array[]): Uint8Array {
  const length = parts.reduce((sum, part) => sum + part.byteLength, 0);
  const output = new Uint8Array(length);
  let offset = 0;
  parts.forEach((part) => {
    output.set(part, offset);
    offset += part.byteLength;
  });
  return output;
}

function toBlob(bytes: Uint8Array, type: string): Blob {
  const copy = new Uint8Array(bytes.byteLength);
  copy.set(bytes);
  return new Blob([copy.buffer], { type });
}

function buildJpegPdf(
  jpeg: Uint8Array,
  imageWidth: number,
  imageHeight: number,
  pageWidth = PDF_PAGE_WIDTH,
  pageHeight = PDF_PAGE_HEIGHT,
): Blob {
  const placement = fitImageOnPage(imageWidth, imageHeight, pageWidth, pageHeight);
  const content = [
    "q",
    `${placement.width.toFixed(2)} 0 0 ${placement.height.toFixed(2)} ${placement.x.toFixed(2)} ${placement.y.toFixed(2)} cm`,
    "/Im1 Do",
    "Q",
  ].join("\n");

  const objects = [
    encodePdfText("1 0 obj\n<< /Type /Catalog /Pages 2 0 R >>\nendobj\n"),
    encodePdfText("2 0 obj\n<< /Type /Pages /Count 1 /Kids [3 0 R] >>\nendobj\n"),
    encodePdfText(
      `3 0 obj\n<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${pageWidth} ${pageHeight}] /Contents 4 0 R /Resources << /XObject << /Im1 5 0 R >> >> >>\nendobj\n`,
    ),
    encodePdfText(`4 0 obj\n<< /Length ${encodePdfText(content).byteLength} >>\nstream\n${content}\nendstream\nendobj\n`),
    concatBytes([
      encodePdfText(
        `5 0 obj\n<< /Type /XObject /Subtype /Image /Width ${imageWidth} /Height ${imageHeight} /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length ${jpeg.byteLength} >>\nstream\n`,
      ),
      jpeg,
      encodePdfText("\nendstream\nendobj\n"),
    ]),
  ];

  const header = concatBytes([
    encodePdfText("%PDF-1.4\n"),
    new Uint8Array([0x25, 0xe2, 0xe3, 0xcf, 0xd3, 0x0a]),
  ]);
  const chunks: Uint8Array[] = [header];
  const offsets = [0];
  let position = header.byteLength;

  objects.forEach((object) => {
    offsets.push(position);
    chunks.push(object);
    position += object.byteLength;
  });

  let xref = `xref\n0 ${objects.length + 1}\n0000000000 65535 f \n`;
  for (let index = 1; index < offsets.length; index += 1) {
    xref += `${String(offsets[index]).padStart(10, "0")} 00000 n \n`;
  }
  xref += `trailer\n<< /Size ${objects.length + 1} /Root 1 0 R >>\nstartxref\n${position}\n%%EOF`;
  chunks.push(encodePdfText(xref));

  return toBlob(concatBytes(chunks), "application/pdf");
}

export type TitledChart = {
  title: string;
  chart: ECharts;
};

const GROUP_GAP = 32;
const GROUP_TITLE_HEIGHT = 36;
const GROUP_PADDING = 24;

function escapeXml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

async function captureTitledCharts(charts: TitledChart[]): Promise<Array<CapturedChart & { title: string }>> {
  if (charts.length === 0) {
    throw new Error("Chart is not ready to export.");
  }

  return Promise.all(charts.map(async (item) => ({
    title: item.title,
    ...(await captureChart(item.chart)),
  })));
}

function groupLayout(charts: { width: number; height: number }[]): { cellWidth: number; width: number; height: number } {
  const cellWidth = Math.max(...charts.map((chart) => chart.width));
  const cellHeight = GROUP_TITLE_HEIGHT + Math.max(...charts.map((chart) => chart.height));

  return {
    cellWidth,
    width: GROUP_PADDING * 2 + charts.length * cellWidth + (charts.length - 1) * GROUP_GAP,
    height: GROUP_PADDING * 2 + cellHeight,
  };
}

export async function downloadChartsAsSvg(charts: TitledChart[], fileName: string): Promise<void> {
  const captured = await captureTitledCharts(charts);
  const layout = groupLayout(captured);
  const panels = captured.map((chart, index) => {
    const x = GROUP_PADDING + index * (layout.cellWidth + GROUP_GAP);
    const imageX = x + (layout.cellWidth - chart.width) / 2;
    const imageY = GROUP_PADDING + GROUP_TITLE_HEIGHT;
    return [
      `<text x="${x}" y="${GROUP_PADDING + 22}" font-size="16" font-family="Arial, sans-serif" fill="#374151">${escapeXml(chart.title)}</text>`,
      `<image href="${chart.dataUrl}" x="${imageX}" y="${imageY}" width="${chart.width}" height="${chart.height}"/>`,
    ].join("");
  }).join("");
  const svg = [
    `<?xml version="1.0" encoding="UTF-8"?>`,
    `<svg xmlns="http://www.w3.org/2000/svg" width="${layout.width}" height="${layout.height}" viewBox="0 0 ${layout.width} ${layout.height}">`,
    `<rect width="100%" height="100%" fill="#ffffff"/>`,
    panels,
    `</svg>`,
  ].join("");

  downloadBlob(new Blob([svg], { type: "image/svg+xml;charset=utf-8" }), `${sanitizeFileName(fileName)}.svg`);
}

export async function downloadChartsAsPdf(charts: TitledChart[], fileName: string): Promise<void> {
  const captured = await captureTitledCharts(charts);
  const layout = groupLayout(captured);
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");
  if (!context) {
    throw new Error("Canvas export is not supported in this browser.");
  }

  canvas.width = layout.width;
  canvas.height = layout.height;
  context.fillStyle = "#ffffff";
  context.fillRect(0, 0, layout.width, layout.height);
  context.fillStyle = "#374151";
  context.font = "16px Arial, sans-serif";
  captured.forEach((chart, index) => {
    const x = GROUP_PADDING + index * (layout.cellWidth + GROUP_GAP);
    context.fillText(chart.title, x, GROUP_PADDING + 22);
    context.drawImage(chart.image, x + (layout.cellWidth - chart.width) / 2, GROUP_PADDING + GROUP_TITLE_HEIGHT);
  });

  const jpeg = await canvasToJpeg(canvas);
  const contentWidth = PDF_PAGE_WIDTH - PDF_MARGIN * 2;
  const pageHeight = Math.max(
    PDF_PAGE_HEIGHT,
    Math.ceil(contentWidth * (canvas.height / canvas.width) + PDF_MARGIN * 2),
  );
  downloadBlob(
    buildJpegPdf(jpeg, canvas.width, canvas.height, PDF_PAGE_WIDTH, pageHeight),
    `${sanitizeFileName(fileName)}.pdf`,
  );
}

export async function downloadChartAsSvg(chart: ECharts, fileName: string): Promise<void> {
  const captured = await captureChart(chart);
  const svg = buildSvgDocument(captured.dataUrl, captured.width, captured.height);
  downloadBlob(new Blob([svg], { type: "image/svg+xml;charset=utf-8" }), `${sanitizeFileName(fileName)}.svg`);
}

type PreparedChart = {
  title: string;
  clone: SVGSVGElement;
  width: number;
  height: number;
};

const SHEET_COLUMNS = 2;
const SHEET_GAP = 32;
const SHEET_TITLE_HEIGHT = 36;
const SHEET_PADDING = 24;

function collectCharts(element: HTMLElement): { title: string; svg: SVGSVGElement }[] {
  if (element instanceof SVGSVGElement) {
    return [{ title: "", svg: element }];
  }

  const fromCards = Array.from(element.querySelectorAll(".card")).flatMap((card) => {
    const svg = card.querySelector("svg");
    if (!(svg instanceof SVGSVGElement)) {
      return [];
    }

    return [{
      title: card.querySelector("h6")?.textContent?.trim() ?? "",
      svg,
    }];
  });

  if (fromCards.length > 0) {
    return fromCards;
  }

  const svg = element.querySelector("svg");
  if (svg instanceof SVGSVGElement) {
    return [{ title: "", svg }];
  }

  throw new Error("Chart SVG was not found.");
}

function prepareSvg(source: SVGSVGElement): { clone: SVGSVGElement; width: number; height: number } {
  const bounds = source.getBoundingClientRect();
  const width = Math.max(1, Math.round(source.width.baseVal.value || bounds.width));
  const height = Math.max(1, Math.round(source.height.baseVal.value || bounds.height));
  const clone = source.cloneNode(true) as SVGSVGElement;
  const background = document.createElementNS("http://www.w3.org/2000/svg", "rect");

  clone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
  clone.setAttribute("width", String(width));
  clone.setAttribute("height", String(height));
  clone.style.width = `${width}px`;
  clone.style.height = `${height}px`;
  if (!clone.getAttribute("viewBox")) {
    clone.setAttribute("viewBox", `0 0 ${width} ${height}`);
  }

  const fontFamily = window.getComputedStyle(source).fontFamily;
  if (fontFamily) {
    clone.setAttribute("font-family", fontFamily);
  }

  clone.querySelectorAll("foreignObject").forEach((node) => node.remove());

  background.setAttribute("width", "100%");
  background.setAttribute("height", "100%");
  background.setAttribute("fill", "#ffffff");
  clone.insertBefore(background, clone.firstChild);

  return { clone, width, height };
}

function svgDocument(svg: SVGSVGElement): string {
  return `<?xml version="1.0" encoding="UTF-8"?>${new XMLSerializer().serializeToString(svg)}`;
}

function sheetLayout(charts: { width: number; height: number }[]): {
  columns: number;
  cellWidth: number;
  cellHeight: number;
  width: number;
  height: number;
} {
  const columns = Math.min(SHEET_COLUMNS, charts.length);
  const rows = Math.ceil(charts.length / columns);
  const cellWidth = Math.max(...charts.map((chart) => chart.width));
  const cellHeight = SHEET_TITLE_HEIGHT + Math.max(...charts.map((chart) => chart.height));

  return {
    columns,
    cellWidth,
    cellHeight,
    width: SHEET_PADDING * 2 + columns * cellWidth + (columns - 1) * SHEET_GAP,
    height: SHEET_PADDING * 2 + rows * cellHeight + (rows - 1) * SHEET_GAP,
  };
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function replaceSvgIdReference(
  value: string,
  attributeName: string,
  id: string,
  nextId: string,
): string {
  const escapedId = escapeRegExp(id);
  const urlReference = new RegExp(`url\\(\\s*(['"]?)#${escapedId}\\1\\s*\\)`, "gi");
  const updated = value.replace(urlReference, (_match, quote: string) => `url(${quote}#${nextId}${quote})`);
  if (attributeName !== "href") {
    return updated;
  }

  return updated.replace(new RegExp(`^#${escapedId}$`), `#${nextId}`);
}

function namespaceSvgIds(svg: SVGSVGElement, prefix: string): void {
  const replacements = Array.from(svg.querySelectorAll("[id]"))
    .flatMap((node) => {
      const id = node.getAttribute("id");
      if (!id) {
        return [];
      }
      const nextId = `${prefix}${id}`;
      node.setAttribute("id", nextId);
      return [[id, nextId] as const];
    })
    .sort((left, right) => right[0].length - left[0].length);

  svg.querySelectorAll("*").forEach((node) => {
    Array.from(node.attributes).forEach((attribute) => {
      if (!attribute.value.includes("#")) {
        return;
      }
      let value = attribute.value;
      replacements.forEach(([id, nextId]) => {
        value = replaceSvgIdReference(value, attribute.localName, id, nextId);
      });
      if (value !== attribute.value) {
        node.setAttribute(attribute.name, value);
      }
    });
  });
}

function buildChartSheet(charts: PreparedChart[]): { markup: string; width: number; height: number } {
  const layout = sheetLayout(charts);
  const sheet = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  const background = document.createElementNS("http://www.w3.org/2000/svg", "rect");

  sheet.setAttribute("xmlns", "http://www.w3.org/2000/svg");
  sheet.setAttribute("width", String(layout.width));
  sheet.setAttribute("height", String(layout.height));
  sheet.setAttribute("viewBox", `0 0 ${layout.width} ${layout.height}`);
  background.setAttribute("width", "100%");
  background.setAttribute("height", "100%");
  background.setAttribute("fill", "#ffffff");
  sheet.appendChild(background);

  charts.forEach((chart, index) => {
    const column = index % layout.columns;
    const row = Math.floor(index / layout.columns);
    const x = SHEET_PADDING + column * (layout.cellWidth + SHEET_GAP);
    const y = SHEET_PADDING + row * (layout.cellHeight + SHEET_GAP);
    const title = document.createElementNS("http://www.w3.org/2000/svg", "text");

    namespaceSvgIds(chart.clone, `c${index}-`);
    title.setAttribute("x", String(x));
    title.setAttribute("y", String(y + 22));
    title.setAttribute("font-size", "16");
    title.setAttribute("font-family", "Arial, sans-serif");
    title.setAttribute("fill", "#374151");
    title.textContent = chart.title;
    chart.clone.setAttribute("x", String(x + (layout.cellWidth - chart.width) / 2));
    chart.clone.setAttribute("y", String(y + SHEET_TITLE_HEIGHT));
    sheet.appendChild(title);
    sheet.appendChild(chart.clone);
  });

  return { markup: svgDocument(sheet), width: layout.width, height: layout.height };
}

async function rasterizeSvg(clone: SVGSVGElement, width: number, height: number): Promise<HTMLCanvasElement> {
  const url = URL.createObjectURL(new Blob([svgDocument(clone)], { type: "image/svg+xml;charset=utf-8" }));
  try {
    const image = await loadImage(url);
    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d");
    if (!context) {
      throw new Error("Canvas export is not supported in this browser.");
    }
    canvas.width = width;
    canvas.height = height;
    context.fillStyle = "#ffffff";
    context.fillRect(0, 0, width, height);
    context.drawImage(image, 0, 0, width, height);
    return canvas;
  } finally {
    URL.revokeObjectURL(url);
  }
}

async function canvasToJpeg(canvas: HTMLCanvasElement): Promise<Uint8Array> {
  const blob = await new Promise<Blob | null>((resolve) => {
    canvas.toBlob(resolve, "image/jpeg", 0.92);
  });
  if (!blob) {
    throw new Error("Could not encode the chart image for PDF.");
  }
  return new Uint8Array(await blob.arrayBuffer());
}

async function composeChartCanvas(charts: PreparedChart[]): Promise<HTMLCanvasElement> {
  const layout = sheetLayout(charts);
  const rasters = await Promise.all(charts.map(async (chart) => ({
    title: chart.title,
    width: chart.width,
    height: chart.height,
    canvas: await rasterizeSvg(chart.clone, chart.width, chart.height),
  })));
  const output = document.createElement("canvas");
  const context = output.getContext("2d");
  if (!context) {
    throw new Error("Canvas export is not supported in this browser.");
  }

  output.width = layout.width;
  output.height = layout.height;
  context.fillStyle = "#ffffff";
  context.fillRect(0, 0, layout.width, layout.height);
  context.fillStyle = "#374151";
  context.font = "16px Arial, sans-serif";
  rasters.forEach((chart, index) => {
    const column = index % layout.columns;
    const row = Math.floor(index / layout.columns);
    const x = SHEET_PADDING + column * (layout.cellWidth + SHEET_GAP);
    const y = SHEET_PADDING + row * (layout.cellHeight + SHEET_GAP);
    context.fillText(chart.title, x, y + 22);
    context.drawImage(
      chart.canvas,
      x + (layout.cellWidth - chart.width) / 2,
      y + SHEET_TITLE_HEIGHT,
    );
  });

  return output;
}

function composeChartSheet(element: HTMLElement): { markup: string; width: number; height: number } {
  const prepared = collectCharts(element).map((chart) => ({
    title: chart.title,
    ...prepareSvg(chart.svg),
  }));

  if (prepared.length === 1 && prepared[0].title === "") {
    return {
      markup: svgDocument(prepared[0].clone),
      width: prepared[0].width,
      height: prepared[0].height,
    };
  }

  return buildChartSheet(prepared);
}

export async function downloadElementAsSvg(element: HTMLElement, fileName: string): Promise<void> {
  const { markup } = composeChartSheet(element);
  downloadBlob(new Blob([markup], { type: "image/svg+xml;charset=utf-8" }), `${sanitizeFileName(fileName)}.svg`);
}

export async function downloadElementAsPdf(element: HTMLElement, fileName: string): Promise<void> {
  const charts = collectCharts(element).map((chart) => ({
    title: chart.title,
    ...prepareSvg(chart.svg),
  }));
  const canvas = charts.length === 1 && charts[0].title === ""
    ? await rasterizeSvg(charts[0].clone, charts[0].width, charts[0].height)
    : await composeChartCanvas(charts);
  const jpeg = await canvasToJpeg(canvas);
  const contentWidth = PDF_PAGE_WIDTH - PDF_MARGIN * 2;
  const pageHeight = Math.max(
    PDF_PAGE_HEIGHT,
    Math.ceil(contentWidth * (canvas.height / canvas.width) + PDF_MARGIN * 2),
  );
  downloadBlob(
    buildJpegPdf(jpeg, canvas.width, canvas.height, PDF_PAGE_WIDTH, pageHeight),
    `${sanitizeFileName(fileName)}.pdf`,
  );
}

export async function downloadChartAsPdf(chart: ECharts, fileName: string): Promise<void> {
  const captured = await captureChart(chart);
  const jpeg = await imageToJpeg(captured.image, captured.width, captured.height);
  const pdf = buildJpegPdf(jpeg, captured.width, captured.height);
  downloadBlob(pdf, `${sanitizeFileName(fileName)}.pdf`);
}
